// ProfileSetup.js
import React, { useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { useNavigate } from 'react-router-dom';
import { finalizeEvent } from 'nostr-tools/pure';
import { hexToBytes } from '@noble/hashes/utils';

const ProfileSetup = () => {
  const { privateKey, publicKey, userMetadata, updateUserMetadata } = useAuth();
  const [name, setName] = useState('');
  const [about, setAbout] = useState('');
  const [picture, setPicture] = useState('');
  const [nip05, setNip05] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isNewAccount, setIsNewAccount] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
	// Check if this is a new account
	setIsNewAccount(!userMetadata);

	// Load existing metadata if available
	if (userMetadata) {
	  setName(userMetadata.name || userMetadata.display_name || '');
	  setAbout(userMetadata.about || '');
	  setPicture(userMetadata.picture || '');
	  setNip05(userMetadata.nip05 || '');
	}
  }, [userMetadata]);

  const handleSubmit = async () => {
	if (!name.trim()) {
	  setError('Name is required');
	  return;
	}

	setIsSubmitting(true);
	setError('');

	try {
	  const metadata = {
		name: name.trim(),
		about: about.trim(),
		picture: picture.trim(),
		nip05: nip05.trim()
	  };

	  const event = {
		kind: 0,
		pubkey: publicKey,
		created_at: Math.floor(Date.now() / 1000),
		tags: [],
		content: JSON.stringify(metadata)
	  };

	  const signedEvent = finalizeEvent(event, hexToBytes(privateKey));

	  const relays = [
		  "wss://relay.nostr.band",
		  "wss://nos.lol",
		  "wss://relay.primal.net",
		  "wss://relay.nos.social",
		  "wss://sources.nostr1.com"
	  ];

	  let publishedSuccessfully = false;

	  for (const relay of relays) {
		const ws = new WebSocket(relay);
		
		try {
		  await new Promise((resolve, reject) => {
			const timeout = setTimeout(() => {
			  ws.close();
			  resolve(false);
			}, 5000);

			ws.onopen = () => {
			  ws.send(JSON.stringify(["EVENT", signedEvent]));
			};

			ws.onmessage = (msg) => {
			  const [type, , status] = JSON.parse(msg.data);
			  if (type === "OK" && status === true) {
				clearTimeout(timeout);
				ws.close();
				resolve(true);
			  }
			};

			ws.onerror = () => {
			  clearTimeout(timeout);
			  resolve(false);
			};
		  });

		  publishedSuccessfully = true;
		} catch (err) {
		  console.error(`Failed to publish to ${relay}:`, err);
		}
	  }

	  if (publishedSuccessfully) {
		updateUserMetadata(metadata);
		
		localStorage.setItem(`user-${publicKey}`, JSON.stringify({
		  data: metadata,
		  timestamp: Date.now()
		}));

		// If this is a new account, show the keys page
		if (isNewAccount) {
		  navigate('/account', { state: { isNewAccount: true }});
		} else {
		  navigate('/account');
		}
	  } else {
		setError('Failed to publish profile updates');
	  }
	} catch (error) {
	  console.error('Failed to update profile:', error);
	  setError('Failed to update profile. Please try again.');
	} finally {
	  setIsSubmitting(false);
	}
  };

  return (
	<div className="profile-setup-container">
	  <div className="profile-setup-card">
		<h2>{isNewAccount ? 'Complete Your Profile' : 'Edit Profile'}</h2>
		
		<div className="profile-setup-form">
		  <div className="form-group">
			<label>Name</label>
			<input
			  type="text"
			  value={name}
			  onChange={(e) => setName(e.target.value)}
			  placeholder="Display Name"
			  className="profile-input"
			/>
		  </div>

		  <div className="form-group">
			<label>About</label>
			<textarea
			  value={about}
			  onChange={(e) => setAbout(e.target.value)}
			  placeholder="Tell us about yourself"
			  className="profile-input"
			  rows={4}
			/>
		  </div>

		  <div className="form-group">
			<label>Profile Picture URL</label>
			<input
			  type="url"
			  value={picture}
			  onChange={(e) => setPicture(e.target.value)}
			  placeholder="https://..."
			  className="profile-input"
			/>
		  </div>

		  

		  {error && <div className="profile-error">{error}</div>}

		  <div className="profile-actions">
			<button 
			  onClick={handleSubmit} 
			  className="profile-button"
			  disabled={isSubmitting}
			>
			  {isSubmitting ? 'Saving...' : 'Save Profile'}
			</button>
			<button 
			  onClick={() => navigate('/account')} 
			  className="profile-button secondary"
			>
			  Cancel
			</button>
		  </div>
		</div>
	  </div>
	</div>
  );
};

export default ProfileSetup;